import { PermissionType } from '@apply/constants';
import moment from 'moment-timezone';
import store from '@apply/adminStore';

const centralTime = (date) => {
    return moment.utc(date).tz('America/Chicago');
};

export const formatDateTime = (date: Date): string => {
    const central = centralTime(date);
    return `${central.format('HH:mm')} CST ${central.format('MM/DD/YYYY')}`;
};

export const formatDate = (date: Date): string => {
    return `${centralTime(date).format('YYYY-MM-DD')}`;
};

export const formatDateMonthDayYear = (date: Date): string => {
    return `${centralTime(date).format('MM-DD-YYYY')}`;
};

export const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const formatCurrency = (value: number, decimals = 2): string => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: decimals,
    });

    return formatter.format(value ?? 0);
};

export const formatCurrencyOrUndefined = (value: number, decimals = 2): string => {
    if (value || value === 0) {
        return formatCurrency(value, decimals);
    }

    return '';
};

export const isDateInFuture = (date: string): boolean => {
    if (!date) return null;

    const acquisitionYearMonthDay = date.replace('-', '').replace('-', '');

    const today = new Date();
    const todayYearMonthDay = `${today.getFullYear()}${(today.getMonth() + 1)
        .toString()
        .padStart(2, '0')}${today.getDate().toString().padStart(2, '0')}`;

    return acquisitionYearMonthDay > todayYearMonthDay; // i.e. '20220619' > '20220630' (YYYYMMDD)
};

export const formatPercent = (val?: number, decimals = 2): string | null => {
    if (val === null || val === undefined || isNaN(val)) {
        return null;
    }
    return (val * 100).toFixed(decimals) + '%';
};

export const formatPhone = (phoneNumberString: string): string | null => {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
};

export const hasPermission = (role: PermissionType) => {
    const { bfiUser } = store.getState();

    if (!bfiUser || !bfiUser.roles) {
        return false;
    }
    return bfiUser.roles.includes(role);
};

export const isPhoneNumberValid = (num: string, required: boolean) => {
    if (!required && !num) {
        return true;
    }

    const rgx = /^\(\d{3}\) \d{3}-\d{4}$/;
    return rgx.test(num);
};

export const detectDeviceType = (): string => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /android/i.test(navigator.userAgent);

    if (isIOS) {
        return 'ios';
    } else if (isAndroid) {
        return 'android';
    } else {
        return 'other';
    }
};

// The 450px width is often used as a benchmark in web and app design
// because it closely aligns with the screen width of many smaller smartphones
// in portrait orientation. It serves as a practical breakpoint for designing
// responsive layouts, ensuring that content displays well on compact devices.
export const isMobileScreen = (): boolean => {
    return window.screen.width < 450;
};

export const detectScreenWidth = (): number => {
    return window.screen.width;
};